/* eslint-disable no-lonely-if */
class Pair {
  first: number;
  second: number;

  constructor(first, second) {
    this.first = first;
    this.second = second;
  }
}

/**
 * Rectangle that more closely matches Java's Rectangle.
 */
class Rect {
  x: number;
  y: number;
  width: number;
  height: number;

  constructor(x, y, width, height) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }
}

// Ported from:
//   - https://github.com/miracle2k/android-platform_sdk/blob/master/ninepatch/src/com/android/ninepatch/NinePatchChunk.java
//
// Originally Apache License
class DrawingData {
  remainderHorizontal: number;
  remainderVertical: number;
  horizontalPatchesSum: number;
  verticalPatchesSum: number;
}

// Ported from:
//   - https://github.com/miracle2k/android-platform_sdk/blob/master/ninepatch/src/com/android/ninepatch/NinePatchChunk.java
//
// Originally Apache License
export class NinePatch {
  private verticalStartWithPatch: boolean;
  private horizontalStartWithPatch: boolean;

  private fixed: Rect[] = [];
  private patches: Rect[] = [];
  private horizontalPatches: Rect[] = [];
  private verticalPatches: Rect[] = [];

  private fixedVertical: number = 0;
  private fixedHorizontal: number = 0;

  constructor(chunk: NinePatchChunk, width: number, height: number) {
    const xPairs = this.getPatches(chunk.xDivs, width);
    this.horizontalStartWithPatch = xPairs.startWithPatch;

    const yPairs = this.getPatches(chunk.yDivs, height);
    this.verticalStartWithPatch = yPairs.startWithPatch;

    // From findPatches

    // These sections are stretchy/fixed in both x and y directions
    this.fixed = this.getRectangles(xPairs.fixed, yPairs.fixed);
    this.patches = this.getRectangles(xPairs.patches, yPairs.patches);

    xPairs.fixed.forEach(x => {
      this.fixedHorizontal += x.second - x.first;
    });
    yPairs.fixed.forEach(y => {
      this.fixedVertical += y.second - y.first;
    });

    if (this.fixed.length > 0) {
      // Patches that are fixed in the x direction and stretchy in the y direction
      this.verticalPatches = this.getRectangles(xPairs.fixed, yPairs.patches);

      // Patches that are fixed in the y direction and stretchy in the x direction
      this.horizontalPatches = this.getRectangles(xPairs.patches, yPairs.fixed);
    } else {
      // TODO(mmastrac): The source we're porting from does weird things with x/y so this needs to be checked
      if (yPairs.fixed.length > 0) {
        // One single column of patches
        // the "horizontal" patches are so called because they stretch horizontally,
        // but they are stacked vertically, hence the mismatch in names
        this.horizontalPatches = this.getVerticalRectangles(width, yPairs.fixed);
        this.verticalPatches = [];
      } else if (xPairs.fixed.length > 0) {
        // One single row of patches
        this.horizontalPatches = [];
        this.verticalPatches = this.getHorizontalRectangles(height, xPairs.fixed);
      } else {
        // Nothing stretchy at all
        this.horizontalPatches = this.verticalPatches = [];
      }
    }
  }

  // Build the set of patches from the parsed divs rather than the image
  private getPatches(
    divsArg: number[],
    size: number,
  ): { fixed: Pair[]; patches: Pair[]; startWithPatch: boolean } {
    let divs = divsArg;
    let fixed: Pair[] = [];
    const patches: Pair[] = [];
    let startWithPatch = false;

    // If the first segment width is zero, that's a flag that the first segment is a patch
    let isPatch = divs[0] == 0;
    if (isPatch) divs = divs.slice(1);

    let last = 0;

    let first = true;
    divs.forEach(n => {
      if (isPatch) {
        if (first) startWithPatch = true;
        patches.push(new Pair(last, n));
      } else {
        fixed.push(new Pair(last, n));
      }

      first = false;
      last = n;
      isPatch = !isPatch;
    });

    // Final segment reaches to end
    if (isPatch) {
      if (first) startWithPatch = true;
      patches.push(new Pair(last, size));
    } else {
      fixed.push(new Pair(last, size));
    }

    // handle cases where the image should just be stretched normally in this direction
    if (patches.length == 0) {
      patches.push(new Pair(0, size));
      fixed = [];
      startWithPatch = true;
    }

    return { fixed, patches, startWithPatch };
  }

  // Port of getRectangles
  private getRectangles(xPairs: Pair[], yPairs: Pair[]): Rect[] {
    const rectangles: Rect[] = [];
    yPairs.forEach(yp => {
      const y = yp.first;
      const height = yp.second - yp.first;

      xPairs.forEach(xp => {
        // These were backwards in the original source
        const x = xp.first;
        const width = xp.second - xp.first;
        rectangles.push(new Rect(x, y, width, height));
      });
    });
    return rectangles;
  }

  // Port of getVerticalRectangles
  // Called when there is only a single stretchy column (from left to right)
  // Patches that are fixed in the x direction and stretchy in the y direction
  private getVerticalRectangles(imageWidth: number, yPairs: Pair[]) {
    const rectangles: Rect[] = [];
    yPairs.forEach(yp => {
      const y = yp.first;
      const height = yp.second - yp.first;
      rectangles.push(new Rect(0, y, imageWidth, height));
    });
    return rectangles;
  }

  // Port of getHorizontalRectangles
  // Called when there is only a single stretchy row (from top to bottom)
  // Patches that are fixed in the y direction and stretchy in the x direction
  private getHorizontalRectangles(imageHeight: number, xPairs: Pair[]) {
    const rectangles: Rect[] = [];
    xPairs.forEach(xp => {
      const x = xp.first;
      const width = xp.second - xp.first;
      rectangles.push(new Rect(x, 0, width, imageHeight));
    });
    return rectangles;
  }

  private computePatches(scaledWidth: number, scaledHeight: number): DrawingData {
    const data = new DrawingData();

    data.remainderHorizontal = scaledWidth - this.fixedHorizontal;
    data.remainderVertical = scaledHeight - this.fixedVertical;

    data.horizontalPatchesSum = 0;
    let hStart = -1;
    const hWhich = this.horizontalPatches.length > 0 ? this.horizontalPatches : this.patches;
    hWhich.forEach(rect => {
      if (rect.x > hStart) {
        data.horizontalPatchesSum += rect.width;
        hStart = rect.x;
      }
    });

    data.verticalPatchesSum = 0;
    let vStart = -1;
    const vWhich = this.verticalPatches.length > 0 ? this.verticalPatches : this.patches;
    vWhich.forEach(rect => {
      if (rect.y > vStart) {
        data.verticalPatchesSum += rect.height;
        vStart = rect.y;
      }
    });

    return data;
  }

  draw(
    image: HTMLImageElement,
    dst: CanvasRenderingContext2D,
    scaledWidth: number,
    scaledHeight: number,
  ) {
    if (scaledWidth <= 0 || scaledHeight <= 0) {
      return;
    }

    if (this.patches.length == 0) {
      dst.drawImage(image, 0, 0, scaledWidth, scaledHeight);
      return;
    }

    let x = 0,
      y = 0;

    const data = this.computePatches(scaledWidth, scaledHeight);

    let fixedIndex = 0;
    let horizontalIndex = 0;
    let verticalIndex = 0;
    let patchIndex = 0;

    let vWeightSum = 1.0;
    let vRemainder = data.remainderVertical;

    let vStretch = this.verticalStartWithPatch;
    while (y < scaledHeight) {
      let hStretch = this.horizontalStartWithPatch;

      let height = 0;
      let vExtra = 0.0;

      let hWeightSum = 1.0;
      let hRemainder = data.remainderHorizontal;

      while (x < scaledWidth) {
        let r: Rect;
        let width;
        if (!vStretch) {
          if (hStretch) {
            r = this.horizontalPatches[horizontalIndex++];
            const extra = r.width / data.horizontalPatchesSum;
            width = ~~((extra * hRemainder) / hWeightSum);
            hWeightSum -= extra;
            hRemainder -= width;
          } else {
            r = this.fixed[fixedIndex++];
            width = r.width;
          }
          dst.drawImage(image, r.x, r.y, r.width, r.height, x, y, width, r.height);
          x += width;
          height = r.height;
        } else {
          if (hStretch) {
            r = this.patches[patchIndex++];
            vExtra = r.height / data.verticalPatchesSum;
            height = ~~((vExtra * vRemainder) / vWeightSum);
            const extra = r.width / data.horizontalPatchesSum;
            width = ~~((extra * hRemainder) / hWeightSum);
            hWeightSum -= extra;
            hRemainder -= width;
          } else {
            r = this.verticalPatches[verticalIndex++];
            vExtra = r.height / data.verticalPatchesSum;
            width = r.width;
            height = ~~((vExtra * vRemainder) / vWeightSum);
          }

          dst.drawImage(image, r.x, r.y, r.width, r.height, x, y, width, height);
          x += width;
        }
        hStretch = !hStretch;
      }
      x = 0;
      y += height;
      if (vStretch) {
        vWeightSum -= vExtra;
        vRemainder -= height;
      }
      vStretch = !vStretch;
    }
  }
}

// Ported from:
//   - https://github.com/rover12421/RsApktool/blob/master/brut.apktool/apktool-lib/src/main/java/brut/androlib/res/decoder/Res9patchStreamDecoder.java
//   - https://android.googlesource.com/platform/frameworks/base/+/master/core/jni/android/graphics/NinePatchImpl.cpp
//
// Originally Apache License
export class NinePatchChunk {
  padLeft: number;
  padRight: number;
  padTop: number;
  padBottom: number;
  xDivs: number[];
  yDivs: number[];
}

export class NinePatchChunkParser {
  private index: number = 0;
  private chunk: NinePatchChunk;
  private data: number[];
  private bigEndian: boolean;

  constructor(data: number[], bigEndian: boolean) {
    this.data = data;
    this.bigEndian = bigEndian;

    const chunk = (this.chunk = new NinePatchChunk());

    this.skipBytes(1);
    const numXDivs = this.readByte();
    const numYDivs = this.readByte();
    this.skipBytes(1);
    this.skipBytes(8);
    chunk.padLeft = this.readInt();
    chunk.padRight = this.readInt();
    chunk.padTop = this.readInt();
    chunk.padBottom = this.readInt();
    this.skipBytes(4);
    chunk.xDivs = this.readIntArray(numXDivs);
    chunk.yDivs = this.readIntArray(numYDivs);
  }

  static parse(data: number[], bigEndian: boolean): NinePatchChunk {
    return new NinePatchChunkParser(data, bigEndian).chunk;
  }

  private skipBytes(n: number) {
    this.index += n;
  }

  private readByte() {
    return this.data[this.index++] & 0xff;
  }

  private readInt() {
    if (this.bigEndian) {
      let n = this.readByte() << 24;
      n |= this.readByte() << 16;
      n |= this.readByte() << 8;
      n |= this.readByte();
      return n;
    } else {
      let n = this.readByte();
      n |= this.readByte() << 8;
      n |= this.readByte() << 16;
      n |= this.readByte() << 24;
      return n;
    }
  }

  private readIntArray(n: number) {
    const a = [];
    for (let i = 0; i < n; i++) {
      a.push(this.readInt());
    }

    return a;
  }
}
